import React, { useState, useEffect } from 'react'
import { apiFetch } from '../services/api'

// =============================================================================
// HELPER: Get robust date (en-CA format for YYYY-MM-DD)
// =============================================================================
const getRobustDate = () => {
  try {
    const d = new Date()
    return d.toLocaleDateString('en-CA')
  } catch (e) {
    console.error('getRobustDate error:', e)
    return '2026-10-21'
  }
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ESTADOS_PAGO = { pagada: 'Pagada', por_pagar: 'Por Pagar' }
const METODOS_PAGO = { transferencia: 'Transferencia', efectivo: 'Efectivo', cheque: 'Cheque' }

const FORM_PRODUCTO_INICIAL = {
  nombre: '',
  codigo: '',
  costo_unitario: '',
  margen_normal: '30',
  margen_mayorista: '15',
  cantidad_minima_mayorista: '6',
  numero_lote: '',
  fecha_vencimiento: '',
}

const FORM_PROVEEDOR_INICIAL = {
  razon_social: '',
  rut: '',
}

const FORM_FACTURA_INICIAL = {
  numero_factura: '',
  proveedor_id: '',
  fecha_emision: getRobustDate(),
  estado_pago: 'por_pagar',
  metodo_pago: 'transferencia',
  detalles: [{ producto_id: '', numero_lote: '', cantidad: '', costo_unitario: '', fecha_vencimiento: '' }],
}

// =============================================================================
// FORMATTING & CALCULATION HELPERS
// =============================================================================

const formatearMoneda = (v) => {
  const num = Number(v) || 0
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num)
}

const calcularPrecioVenta = (costo, margen) => {
  try {
    const costoNum = parseFloat(costo) || 0
    const margenNum = parseFloat(margen) || 0
    if (isNaN(costoNum) || isNaN(margenNum)) return 0
    const result = costoNum * (1 + margenNum / 100)
    return isFinite(result) && result >= 0 ? result : 0
  } catch (e) {
    console.warn('Error en calcularPrecioVenta:', e)
    return 0
  }
}

const calcularTotal = (detalles) => {
  try {
    if (!Array.isArray(detalles)) return 0
    const total = detalles.reduce((sum, d) => {
      if (!d || typeof d !== 'object') return sum
      const qty = Number(d?.cantidad) || 0
      const cost = Number(d?.costo_unitario) || 0
      const subtotal = qty * cost
      return sum + (isFinite(subtotal) ? subtotal : 0)
    }, 0)
    return isFinite(total) ? total : 0
  } catch (e) {
    console.warn('Error en calcularTotal:', e)
    return 0
  }
}

// =============================================================================
// ERROR BOUNDARY WRAPPER
// =============================================================================
class FacturasErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorStack: [] }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] CRASH:', error)
    console.error('[ErrorBoundary] Stack:', errorInfo?.componentStack)
    try {
      const stackLines = (error?.stack || '').split('\n').slice(0, 5)
      this.setState({ errorStack: stackLines })
    } catch (e) {
      console.error('[ErrorBoundary] Could not parse stack:', e)
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', background: '#fee2e2', borderRadius: '8px', margin: '1rem' }}>
          <h2 style={{ color: '#991b1b', margin: '0 0 1rem 0' }}>Error en Facturas.jsx</h2>
          <div style={{ fontSize: '0.875rem', color: '#7f1d1d', fontFamily: 'monospace' }}>
            {this.state.error?.message}
            {this.state.errorStack.length > 0 && (
              <div style={{ marginTop: '1rem', background: '#fff', padding: '0.75rem', borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {this.state.errorStack.join('\n')}
              </div>
            )}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#991b1b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Recargar página
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default function FacturasWrapper() {
  return (
    <FacturasErrorBoundary>
      <Facturas />
    </FacturasErrorBoundary>
  )
}

function Facturas() {
  console.log('[Facturas] 🟢 MOUNTED at', new Date().toISOString())

  // =========================================================================
  // STATE: UI & Loading
  // =========================================================================
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')

  console.log('[Facturas] After loading state:', { loading, error, exito })

  // =========================================================================
  // STATE: Modal Controls
  // =========================================================================
  const [modalNuevo, setModalNuevo] = useState(false)
  const [modalNuevoProveedor, setModalNuevoProveedor] = useState(false)
  const [modalNuevoProducto, setModalNuevoProducto] = useState(null) // null or index of detalle

  console.log('[Facturas] After modal states:', { modalNuevo, modalNuevoProveedor, modalNuevoProducto })

  // =========================================================================
  // STATE: Saving States
  // =========================================================================
  const [guardando, setGuardando] = useState(false)
  const [guardandoProveedor, setGuardandoProveedor] = useState(false)
  const [guardandoProducto, setGuardandoProducto] = useState(false)

  // =========================================================================
  // STATE: Modal Form Errors
  // =========================================================================
  const [errorProveedor, setErrorProveedor] = useState('')
  const [errorProducto, setErrorProducto] = useState('')

  // =========================================================================
  // STATE: Data
  // =========================================================================
  const [productos, setProductos] = useState([])
  const [proveedores, setProveedores] = useState([])

  console.log('[Facturas] After data states:', { productosCount: productos.length, proveedoresCount: proveedores.length })

  // =========================================================================
  // STATE: Forms
  // =========================================================================
  const [form, setForm] = useState(FORM_FACTURA_INICIAL)
  const [formNuevoProveedor, setFormNuevoProveedor] = useState(FORM_PROVEEDOR_INICIAL)
  const [formNuevoProducto, setFormNuevoProducto] = useState(FORM_PRODUCTO_INICIAL)

  console.log('[Facturas] After form states:', { form, formNuevoProveedor, formNuevoProducto })

  // ==========================================================================
  // EFFECTS
  // ==========================================================================
  useEffect(() => {
    console.log('[Facturas.useEffect] Starting cargarDatos...')
    cargarDatos()
  }, [])

  // ==========================================================================
  // HELPERS: Data Loading
  // ==========================================================================
  const cargarDatos = async () => {
    console.log('[cargarDatos] START: Setting loading=true')
    setLoading(true)

    try {
      console.log('[cargarDatos] Fetching /inventario')
      const prodsData = await apiFetch('/inventario').catch((err) => {
        console.error('[cargarDatos] /inventario error:', err)
        return {}
      })
      console.log('[cargarDatos] /inventario response:', prodsData)

      console.log('[cargarDatos] Fetching /contactos?tipo=proveedor')
      const provData = await apiFetch('/contactos?tipo=proveedor').catch((err) => {
        console.error('[cargarDatos] /contactos error:', err)
        return {}
      })
      console.log('[cargarDatos] /contactos response:', provData)

      // Parse productos
      let productosList = []
      try {
        console.log('[cargarDatos] Parsing productos...')
        if (Array.isArray(prodsData)) {
          console.log('[cargarDatos] prodsData is array, using directly')
          productosList = prodsData
        } else if (prodsData && typeof prodsData === 'object') {
          if (Array.isArray(prodsData.productos)) {
            console.log('[cargarDatos] prodsData.productos is array')
            productosList = prodsData.productos
          } else if (Array.isArray(prodsData.items)) {
            console.log('[cargarDatos] prodsData.items is array')
            productosList = prodsData.items
          }
        }
        productosList = (productosList || []).filter(p => p && typeof p === 'object')
        console.log('[cargarDatos] Parsed productos count:', productosList.length)
      } catch (e) {
        console.error('[cargarDatos] Error parsing productos:', e?.message)
        productosList = []
      }

      // Parse proveedores
      let proveedoresList = []
      try {
        console.log('[cargarDatos] Parsing proveedores...')
        if (Array.isArray(provData)) {
          console.log('[cargarDatos] provData is array, using directly')
          proveedoresList = provData
        } else if (provData && typeof provData === 'object') {
          if (Array.isArray(provData.contactos)) {
            console.log('[cargarDatos] provData.contactos is array')
            proveedoresList = provData.contactos
          }
        }
        proveedoresList = (proveedoresList || []).filter(p => p && typeof p === 'object')
        console.log('[cargarDatos] Parsed proveedores count:', proveedoresList.length)
      } catch (e) {
        console.error('[cargarDatos] Error parsing proveedores:', e?.message)
        proveedoresList = []
      }

      console.log('[cargarDatos] Setting state with:', { productosList: productosList.length, proveedoresList: proveedoresList.length })
      setProductos(productosList)
      setProveedores(proveedoresList)
      console.log('[cargarDatos] State updated')
    } catch (e) {
      console.error('[cargarDatos] Unexpected error:', e?.message, e?.stack)
      setProductos([])
      setProveedores([])
    } finally {
      console.log('[cargarDatos] END: Setting loading=false')
      setLoading(false)
    }
  }

  // ==========================================================================
  // EVENT HANDLERS: PROVIDER (Proveedor)
  // ==========================================================================
  const handleCrearProveedor = async (e) => {
    console.log('[handleCrearProveedor] START')
    e.preventDefault()
    setErrorProveedor('')

    const razonSocial = (formNuevoProveedor?.razon_social || '').trim()
    const rut = (formNuevoProveedor?.rut || '').trim()

    console.log('[handleCrearProveedor] Form values:', { razonSocial, rut })

    if (!razonSocial) {
      console.log('[handleCrearProveedor] Validation failed: razonSocial empty')
      setErrorProveedor('Razón Social requerida')
      return
    }
    if (!rut) {
      console.log('[handleCrearProveedor] Validation failed: rut empty')
      setErrorProveedor('RUT requerido')
      return
    }

    setGuardandoProveedor(true)
    try {
      console.log('[handleCrearProveedor] Posting to /contactos')
      const nuevoProveedor = await apiFetch('/contactos', {
        method: 'POST',
        body: JSON.stringify({
          razon_social: razonSocial,
          rut: rut,
          tipo: 'proveedor',
        }),
      }).catch((err) => {
        console.error('[handleCrearProveedor] POST error:', err)
        return null
      })

      console.log('[handleCrearProveedor] Response:', nuevoProveedor)

      if (!nuevoProveedor || typeof nuevoProveedor !== 'object') {
        console.error('[handleCrearProveedor] Invalid response type')
        setErrorProveedor('Respuesta inválida del servidor')
        return
      }

      const provId = nuevoProveedor?.id
      if (!provId) {
        console.error('[handleCrearProveedor] No ID in response')
        setErrorProveedor('No se pudo obtener ID del proveedor')
        return
      }

      console.log('[handleCrearProveedor] Updating state, provId:', provId)
      setProveedores(prev => [...(prev || []), nuevoProveedor])
      setForm(prev => ({
        ...(prev || {}),
        proveedor_id: String(provId)
      }))

      console.log('[handleCrearProveedor] Closing modal')
      setModalNuevoProveedor(false)
      setFormNuevoProveedor(FORM_PROVEEDOR_INICIAL)
      console.log('[handleCrearProveedor] DONE')
    } catch (err) {
      console.error('[handleCrearProveedor] Exception:', err?.message)
      setErrorProveedor((err?.message) || 'Error creando proveedor')
    } finally {
      setGuardandoProveedor(false)
    }
  }

  // ==========================================================================
  // EVENT HANDLERS: PRODUCT (Producto)
  // ==========================================================================
  const handleCrearProducto = async (e) => {
    console.log('[handleCrearProducto] START')
    e.preventDefault()
    setErrorProducto('')

    const formProd = formNuevoProducto || {}
    const nombre = (formProd.nombre || '').trim()
    const codigo = (formProd.codigo || '').trim()
    const costoStr = (formProd.costo_unitario || '').toString().trim()
    const numeroLote = (formProd.numero_lote || '').trim()
    const fechaVencimiento = (formProd.fecha_vencimiento || '').trim()

    console.log('[handleCrearProducto] Form values:', { nombre, codigo, costoStr, numeroLote, fechaVencimiento })

    if (!nombre) {
      console.log('[handleCrearProducto] Validation failed: nombre empty')
      setErrorProducto('Nombre requerido')
      return
    }
    if (!codigo) {
      console.log('[handleCrearProducto] Validation failed: codigo empty')
      setErrorProducto('Código/SKU requerido')
      return
    }
    if (!costoStr || Number(costoStr) <= 0) {
      console.log('[handleCrearProducto] Validation failed: costo invalid')
      setErrorProducto('Costo unitario requerido y debe ser > 0')
      return
    }
    if (!numeroLote) {
      console.log('[handleCrearProducto] Validation failed: numeroLote empty')
      setErrorProducto('Número de lote requerido')
      return
    }
    if (!fechaVencimiento) {
      console.log('[handleCrearProducto] Validation failed: fechaVencimiento empty')
      setErrorProducto('Fecha de vencimiento requerida')
      return
    }

    setGuardandoProducto(true)
    try {
      console.log('[handleCrearProducto] Calculating prices...')
      const costoNum = Number(costoStr) || 0
      const precioVenta = calcularPrecioVenta(costoNum, formProd.margen_normal || '30')
      const precioMayorista = calcularPrecioVenta(costoNum, formProd.margen_mayorista || '15')
      const cantMinMayorista = parseInt(formProd.cantidad_minima_mayorista || '6', 10) || 6

      console.log('[handleCrearProducto] Prices:', { precioVenta, precioMayorista, cantMinMayorista })

      const nuevoProducto = {
        nombre: nombre,
        codigo: codigo,
        precio_compra: costoNum,
        precio_venta: precioVenta,
        precio_mayorista: precioMayorista,
        cantidad_mayorista: Math.max(1, cantMinMayorista),
        stock_actual: 0,
        stock_minimo: 0,
        activo: true,
        es_nuevo: true,
        numero_lote: numeroLote,
        fecha_vencimiento: fechaVencimiento,
      }

      console.log('[handleCrearProducto] Posting to /productos')
      let productoGuardado = nuevoProducto
      try {
        const respuesta = await apiFetch('/productos', {
          method: 'POST',
          body: JSON.stringify(nuevoProducto),
        }).catch((err) => {
          console.error('[handleCrearProducto] POST error:', err)
          return null
        })

        console.log('[handleCrearProducto] Response:', respuesta)

        if (respuesta && typeof respuesta === 'object' && respuesta.id) {
          console.log('[handleCrearProducto] Got ID from server:', respuesta.id)
          productoGuardado = respuesta
        } else {
          console.log('[handleCrearProducto] Creating local ID')
          productoGuardado = { ...nuevoProducto, id: `prod-${Date.now()}` }
        }
      } catch (err) {
        console.error('[handleCrearProducto] Exception posting:', err?.message)
        productoGuardado = { ...nuevoProducto, id: `prod-${Date.now()}` }
      }

      console.log('[handleCrearProducto] Updating productos state')
      setProductos(prev => [...(prev || []), productoGuardado])

      const detalleIdx = modalNuevoProducto
      console.log('[handleCrearProducto] modalNuevoProducto:', detalleIdx)
      if (typeof detalleIdx === 'number' && detalleIdx >= 0) {
        console.log('[handleCrearProducto] Updating detalle at index:', detalleIdx)
        setForm(prev => {
          const currentForm = prev || {}
          const currentDetalles = Array.isArray(currentForm.detalles) ? currentForm.detalles : []

          if (detalleIdx >= currentDetalles.length) {
            console.error(`[handleCrearProducto] Index ${detalleIdx} out of range for detalles array (len=${currentDetalles.length})`)
            return currentForm
          }

          const newDetalles = [...currentDetalles]
          const currentDetalle = newDetalles[detalleIdx] || {}

          newDetalles[detalleIdx] = {
            ...currentDetalle,
            producto_id: String(productoGuardado?.id || ''),
            costo_unitario: costoNum,
            numero_lote: numeroLote,
            fecha_vencimiento: fechaVencimiento,
            es_nuevo: true,
            producto_nuevo: {
              nombre: nombre,
              codigo: codigo,
              precio_compra: costoNum,
              precio_venta: precioVenta,
              precio_mayorista: precioMayorista,
              cantidad_mayorista: Math.max(1, cantMinMayorista),
            },
          }
          return { ...(currentForm || {}), detalles: newDetalles }
        })
      }

      console.log('[handleCrearProducto] Closing modal')
      setModalNuevoProducto(null)
      setFormNuevoProducto(FORM_PRODUCTO_INICIAL)
      console.log('[handleCrearProducto] DONE')
    } catch (err) {
      console.error('[handleCrearProducto] Exception:', err?.message, err?.stack)
      setErrorProducto((err?.message) || 'Error creando producto')
    } finally {
      setGuardandoProducto(false)
    }
  }

  // ==========================================================================
  // EVENT HANDLERS: DETAIL LINES (Detalles de factura)
  // ==========================================================================
  const handleAgregarDetalle = () => {
    setForm(prev => {
      const currentForm = prev || {}
      const currentDetalles = Array.isArray(currentForm.detalles) ? currentForm.detalles : []
      return {
        ...(currentForm || {}),
        detalles: [...currentDetalles, { producto_id: '', numero_lote: '', cantidad: '', costo_unitario: '', fecha_vencimiento: '' }]
      }
    })
  }

  const handleEliminarDetalle = (idx) => {
    setForm(prev => {
      const currentForm = prev || {}
      const currentDetalles = Array.isArray(currentForm.detalles) ? currentForm.detalles : []
      return {
        ...(currentForm || {}),
        detalles: currentDetalles.filter((_, i) => i !== idx)
      }
    })
  }

  const handleDetalleChange = (idx, field, value) => {
    setForm(prev => {
      const currentForm = prev || {}
      const currentDetalles = Array.isArray(currentForm.detalles) ? currentForm.detalles : []
      const newDetalles = [...currentDetalles]

      if (typeof idx === 'number' && idx >= 0 && idx < newDetalles.length) {
        newDetalles[idx] = { ...(newDetalles[idx] || {}), [field]: value }
      }

      return { ...(currentForm || {}), detalles: newDetalles }
    })
  }

  // ==========================================================================
  // EVENT HANDLERS: MAIN FORM (Save invoice)
  // ==========================================================================
  const handleGuardar = async (e) => {
    console.log('[handleGuardar] START')
    e.preventDefault()
    setError('')
    setExito('')

    const currentForm = form || {}
    const numFactura = (currentForm.numero_factura || '').trim()
    const detalles = Array.isArray(currentForm.detalles) ? currentForm.detalles : []
    const fechaEmision = (currentForm.fecha_emision || '').trim()
    const estadoPago = (currentForm.estado_pago || 'por_pagar').toString()
    const metodoPago = (currentForm.metodo_pago || 'transferencia').toString()
    const proveedorId = (currentForm.proveedor_id || '').toString().trim()

    console.log('[handleGuardar] Form values:', { numFactura, detallesCount: detalles.length, fechaEmision, estadoPago, metodoPago, proveedorId })

    // Validation
    if (!numFactura) {
      console.log('[handleGuardar] Validation failed: numFactura empty')
      setError('Número de factura requerido')
      return
    }
    if (detalles.length === 0) {
      console.log('[handleGuardar] Validation failed: no detalles')
      setError('Debes agregar al menos un producto')
      return
    }

    console.log('[handleGuardar] Validating detalles...')
    const detallesValidos = detalles.every((d, idx) => {
      if (!d || typeof d !== 'object') {
        console.log(`[handleGuardar] Detalle ${idx}: not an object`)
        return false
      }
      const prodId = (d.producto_id || '').toString().trim()
      const lote = (d.numero_lote || '').trim()
      const cant = Number(d?.cantidad) || 0
      const costo = Number(d?.costo_unitario) || 0
      const fecha = (d.fecha_vencimiento || '').trim()
      const valid = prodId && lote && cant > 0 && costo > 0 && fecha
      if (!valid) {
        console.log(`[handleGuardar] Detalle ${idx}: invalid - prodId=${!!prodId} lote=${!!lote} cant=${cant} costo=${costo} fecha=${!!fecha}`)
      }
      return valid
    })

    if (!detallesValidos) {
      console.log('[handleGuardar] Validation failed: detalles not valid')
      setError('Completa todos los campos de los productos (todos deben ser > 0)')
      return
    }

    console.log('[handleGuardar] Validation passed, building payload...')
    setGuardando(true)
    try {
      const detallesPayload = detalles.map((d, idx) => {
        const prodId = Number(d?.producto_id) || 0
        const cantidad = Number(d?.cantidad) || 0
        const costoUnit = Number(d?.costo_unitario) || 0
        const esNuevo = (d?.es_nuevo === true)

        console.log(`[handleGuardar] Detalle ${idx}:`, { prodId, cantidad, costoUnit, esNuevo })

        return {
          producto_id: prodId,
          numero_lote: (d?.numero_lote || '').toString(),
          cantidad: cantidad,
          costo_unitario: costoUnit,
          fecha_vencimiento: (d?.fecha_vencimiento || '').toString(),
          es_nuevo: esNuevo,
          ...(esNuevo && d?.producto_nuevo && typeof d.producto_nuevo === 'object'
            ? { producto_nuevo: d.producto_nuevo }
            : {}),
        }
      }).filter(d => d.producto_id > 0)

      console.log('[handleGuardar] Detalles payload count:', detallesPayload.length)

      if (detallesPayload.length === 0) {
        console.log('[handleGuardar] No valid detalles in payload')
        setError('No hay detalles válidos para guardar')
        return
      }

      const totalFactura = calcularTotal(detalles)
      console.log('[handleGuardar] Total calculado:', totalFactura)

      const payload = {
        numero_factura: numFactura,
        proveedor_id: proveedorId ? Number(proveedorId) : null,
        fecha_emision: fechaEmision,
        total: totalFactura,
        estado_pago: estadoPago,
        metodo_pago: metodoPago,
        detalles: detallesPayload,
      }

      console.log('[handleGuardar] Posting to /compras/factura:', payload)

      const respuesta = await apiFetch('/compras/factura', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).catch((err) => {
        console.error('[handleGuardar] POST error:', err)
        throw err
      })

      console.log('[handleGuardar] Response:', respuesta)

      if (!respuesta) {
        console.error('[handleGuardar] Empty response')
        setError('Respuesta vacía del servidor')
        return
      }

      console.log('[handleGuardar] Success, closing modal and reloading')
      setExito(`Factura ${numFactura} registrada exitosamente`)
      setModalNuevo(false)
      setForm(FORM_FACTURA_INICIAL)
      await cargarDatos()
      console.log('[handleGuardar] DONE')
    } catch (err) {
      console.error('[handleGuardar] Exception:', err?.message, err?.stack)
      setError((err?.message) || 'Error registrando factura')
    } finally {
      setGuardando(false)
    }
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================
  console.log('[Facturas] RENDERING - loading:', loading, 'error:', error, 'exito:', exito)

  if (loading) {
    console.log('[Facturas] Rendering loading state')
    return <div className="main-content" style={{ padding: '2rem' }}>Cargando...</div>
  }

  console.log('[Facturas] Rendering main content')
  return (
    <div className="main-content" style={{ padding: '2rem' }}>
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
              Ingreso por Factura
            </h1>
            <p className="page-subtitle" style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              Registra compras de proveedores y crea lotes de inventario
            </p>
          </div>
          <button
            onClick={() => setModalNuevo(true)}
            style={{
              backgroundColor: '#2563eb', color: '#fff', border: 'none',
              padding: '0.6rem 1.2rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            + Nueva Factura
          </button>
        </div>
      </div>

      {/* Success Message */}
      {exito && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1rem', color: '#15803d', fontSize: '0.875rem' }}>
          {exito}
          <button onClick={() => setExito('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#15803d', fontWeight: '700' }}>✕</button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: '700' }}>✕</button>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: Nueva Factura */}
      {/* ===================================================================== */}
      {modalNuevo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#0f172a' }}>Registrar Nueva Factura</h2>

            <form onSubmit={handleGuardar}>
              {/* Datos principales */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Número Factura *</label>
                  <input
                    type="text" required
                    placeholder="F-2026-001"
                    value={form?.numero_factura || ''}
                    onChange={e => setForm({ ...(form || {}), numero_factura: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Fecha Emisión *</label>
                  <input
                    type="date" required
                    value={form?.fecha_emision || getRobustDate()}
                    onChange={e => setForm({ ...(form || {}), fecha_emision: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Proveedor (opcional)</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={(form || {})?.proveedor_id || ''}
                      onChange={e => setForm({ ...(form || {}), proveedor_id: e.target.value })}
                      style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="">— Selecciona proveedor —</option>
                      {(proveedores || []).filter(p => p && typeof p === 'object' && p.id).map(p => (
                        <option key={p?.id} value={p?.id}>{(p?.razon_social || p?.nombre || 'Sin nombre')}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setModalNuevoProveedor(true)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#e0e7ff',
                        color: '#4f46e5',
                        border: '1px solid #c7d2fe',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      + Nuevo
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Estado Pago</label>
                  <select
                    value={form?.estado_pago || 'por_pagar'}
                    onChange={e => setForm({ ...(form || {}), estado_pago: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  >
                    {Object.entries(ESTADOS_PAGO || {}).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Método Pago</label>
                  <select
                    value={form?.metodo_pago || 'transferencia'}
                    onChange={e => setForm({ ...(form || {}), metodo_pago: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  >
                    {Object.entries(METODOS_PAGO || {}).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Detalles de productos */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#0f172a' }}>Productos</h3>
                {(form?.detalles || []).map((detalle, idx) => {
                  const d = detalle || {}
                  return (
                    <div key={`detalle-${idx}`} style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Producto *</label>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <select
                              value={d?.producto_id || ''}
                              onChange={e => handleDetalleChange(idx, 'producto_id', e.target.value)}
                              style={{ flex: 1, padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                            >
                              <option value="">— Selecciona —</option>
                              {(productos || []).filter(p => p && typeof p === 'object' && p.id).map(p => (
                                <option key={p?.id} value={p?.id}>{(p?.nombre || 'Sin nombre')}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setModalNuevoProducto(idx)}
                              style={{
                                padding: '0.4rem 0.5rem',
                                background: '#dbeafe',
                                color: '#0284c7',
                                border: '1px solid #bae6fd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              + Crear
                            </button>
                          </div>
                        </div>

                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Lote *</label>
                          <input
                            type="text" placeholder="L-001"
                            value={d?.numero_lote || ''}
                            onChange={e => handleDetalleChange(idx, 'numero_lote', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Cantidad *</label>
                          <input
                            type="number" min="1" placeholder="0"
                            value={d?.cantidad || ''}
                            onChange={e => handleDetalleChange(idx, 'cantidad', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Costo Unit. *</label>
                          <input
                            type="number" min="0" step="0.01" placeholder="0.00"
                            value={d?.costo_unitario || ''}
                            onChange={e => handleDetalleChange(idx, 'costo_unitario', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Vencimiento *</label>
                          <input
                            type="date"
                            value={d?.fecha_vencimiento || ''}
                            onChange={e => handleDetalleChange(idx, 'fecha_vencimiento', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem' }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => handleEliminarDetalle(idx)}
                            style={{ padding: '0.4rem 0.6rem', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}

                <button
                  type="button"
                  onClick={handleAgregarDetalle}
                  style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem', width: '100%' }}
                >
                  + Agregar Producto
                </button>
              </div>

              {/* Total */}
              <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '6px', marginBottom: '1.5rem', border: '1px solid #bbf7d0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: '600', color: '#065f46' }}>
                  <span>Total Factura:</span>
                  <span>{formatearMoneda(calcularTotal())}</span>
                </div>
              </div>

              {/* Botones */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setModalNuevo(false)}
                  style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardando}
                  style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {guardando ? 'Guardando...' : 'Registrar Factura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: Nuevo Proveedor */}
      {/* ===================================================================== */}
      {modalNuevoProveedor && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#0f172a' }}>Nuevo Proveedor</h2>

            {errorProveedor && (
              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>
                {errorProveedor}
              </div>
            )}

            <form onSubmit={handleCrearProveedor}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Razón Social *</label>
                <input
                  type="text" required
                  placeholder="Ej: Acme S.A."
                  value={formNuevoProveedor?.razon_social || ''}
                  onChange={e => setFormNuevoProveedor({ ...(formNuevoProveedor || {}), razon_social: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>RUT *</label>
                <input
                  type="text" required
                  placeholder="Ej: 12345678-9"
                  value={formNuevoProveedor?.rut || ''}
                  onChange={e => setFormNuevoProveedor({ ...(formNuevoProveedor || {}), rut: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setModalNuevoProveedor(false)
                    setFormNuevoProveedor(FORM_PROVEEDOR_INICIAL)
                    setErrorProveedor('')
                  }}
                  style={{ flex: 1, padding: '0.5rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardandoProveedor}
                  style={{ flex: 1, padding: '0.5rem', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {guardandoProveedor ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: Crear Producto Rápido */}
      {/* ===================================================================== */}
      {modalNuevoProducto !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#0f172a' }}>Crear Producto Rápido</h2>

            {errorProducto && (
              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>
                {errorProducto}
              </div>
            )}

            <form onSubmit={handleCrearProducto}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Nombre *</label>
                  <input
                    type="text" required
                    placeholder="Ej: Leche Entera"
                    value={formNuevoProducto?.nombre || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), nombre: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Código/SKU *</label>
                  <input
                    type="text" required
                    placeholder="Ej: SKU-001 (escaneable)"
                    value={formNuevoProducto?.codigo || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), codigo: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Costo Unitario *</label>
                  <input
                    type="number" min="0" step="0.01" required
                    placeholder="0.00"
                    value={formNuevoProducto?.costo_unitario || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), costo_unitario: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Margen Normal (%)</label>
                  <input
                    type="number" min="0" step="0.01"
                    placeholder="30"
                    value={formNuevoProducto?.margen_normal || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), margen_normal: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Margen Mayorista (%)</label>
                  <input
                    type="number" min="0" step="0.01"
                    placeholder="15"
                    value={formNuevoProducto?.margen_mayorista || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), margen_mayorista: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Cantidad Mín. Mayorista</label>
                  <input
                    type="number" min="1" step="1"
                    placeholder="6"
                    value={formNuevoProducto?.cantidad_minima_mayorista || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), cantidad_minima_mayorista: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Número Lote *</label>
                  <input
                    type="text" required
                    placeholder="Ej: L-2026-001"
                    value={formNuevoProducto?.numero_lote || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), numero_lote: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>Fecha Vencimiento *</label>
                  <input
                    type="date" required
                    value={formNuevoProducto?.fecha_vencimiento || ''}
                    onChange={e => setFormNuevoProducto({ ...(formNuevoProducto || {}), fecha_vencimiento: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              {/* Vista previa de precios */}
              {(formNuevoProducto?.costo_unitario) && (
                <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #bbf7d0', fontSize: '0.875rem' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Costo:</strong> {formatearMoneda(Number(formNuevoProducto?.costo_unitario) || 0)}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Precio Venta (Normal):</strong> {formatearMoneda(calcularPrecioVenta(formNuevoProducto?.costo_unitario || 0, formNuevoProducto?.margen_normal || '30'))}
                  </div>
                  <div>
                    <strong>Precio Mayorista:</strong> {formatearMoneda(calcularPrecioVenta(formNuevoProducto?.costo_unitario || 0, formNuevoProducto?.margen_mayorista || '15'))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setModalNuevoProducto(null)
                    setFormNuevoProducto(FORM_PRODUCTO_INICIAL)
                    setErrorProducto('')
                  }}
                  style={{ flex: 1, padding: '0.5rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardandoProducto}
                  style={{ flex: 1, padding: '0.5rem', background: '#0284c7', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {guardandoProducto ? 'Guardando...' : 'Crear y Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
